function wordCounter() {
    return {
        text: '',
        showAdvanced: true,
        ignoreStopWords: true, // Default enabled for better keyword analysis
        copied: false,
        stats: {
            words: 0, uniqueWords: 0, chars: 0, 
            sentences: 0, paragraphs: 0, lines: 0,
            avgWordLength: 0, avgSentenceLength: 0,
            readTime: '0 sec', speakTime: '0 sec', fleschScore: 'N/A'
        },
        topWords: [],
        top2Grams: [],
        
        stopWordsList: ['a','an','the','and','or','but','in','on','at','to','of','is','are','was','were','it','that','this','with','for','as','by','be','from','at','which','or','have','not','are'],

        init() {
            this.$watch('text', () => this.update());
        },

        formatNumber(num) {
            return new Intl.NumberFormat().format(num);
        },

        copyText() {
            if(!this.text) return;
            navigator.clipboard.writeText(this.text);
            this.copied = true;
            setTimeout(() => this.copied = false, 2000);
        },

        clearText() {
            this.text = '';
        },

        getPercent(current, target) {
             return Math.min((current / target) * 100, 100);
        },

        getColor(current, target) {
            const pct = (current / target) * 100;
            if (pct >= 100) return 'success';
            if (pct >= 80) return 'warning';
            return ''; // default dictionary color
        },
        
        countSyllables(word) {
            word = word.toLowerCase();
            if(word.length <= 3) return 1;
            word = word.replace(/(?:[^laeiouy]es|ed|[^laeiouy]e)$/, '');
            word = word.replace(/^y/, '');
            const syllables = word.match(/[aeiouy]{1,2}/g);
            return syllables ? syllables.length : 1;
        },

        update() {
            if (!this.text) {
                this.stats = { words: 0, uniqueWords: 0, chars: 0, sentences: 0, paragraphs: 0, lines: 0, avgWordLength: 0, avgSentenceLength: 0, readTime: '0 sec', speakTime: '0 sec', fleschScore: 'N/A' };
                this.topWords = [];
                this.top2Grams = [];
                return;
            }

            const raw = this.text;
            
            // Core Counts
            const wordsArray = raw.trim().split(/\s+/).filter(w => w.length > 0);
            const words = wordsArray.length;
            const chars = raw.length;
            const lines = raw.split(/\r\n|\r|\n/).length;
            const paragraphs = raw.split(/\n\s*\n/).filter(p => p.trim().length > 0).length;
            const sentences = raw.split(/[.!?]+/).filter(s => s.trim().length > 0).length || 1;

            // Unique Words & Avg Length
            const uniqueWordsSet = new Set(wordsArray.map(w => w.toLowerCase()));
            const uniqueWords = uniqueWordsSet.size;
            const charsNoSpaces = raw.replace(/\s/g, '').length;
            const avgWordLength = words > 0 ? (charsNoSpaces / words).toFixed(1) : 0;
            const avgSentenceLength = sentences > 0 ? (words / sentences).toFixed(1) : 0;

            // Readability
            const readTimeMin = words / 200;
            let readTimeDisplay = readTimeMin < 1 ? Math.ceil(readTimeMin * 60) + ' sec' : Math.ceil(readTimeMin) + ' min';
            const speakTimeMin = words / 130;
            let speakTimeDisplay = speakTimeMin < 1 ? Math.ceil(speakTimeMin * 60) + ' sec' : Math.ceil(speakTimeMin) + ' min';

            // Flesch Score
            let flesch = 'N/A';
            if (words > 0 && sentences > 0) {
                const totalSyllables = wordsArray.reduce((acc, w) => acc + this.countSyllables(w), 0);
                const asl = words / sentences;
                const asw = totalSyllables / words;
                const score = 206.835 - (1.015 * asl) - (84.6 * asw);
                flesch = Math.max(0, Math.min(100, Math.round(score)));
            }

            this.stats = {
                words, uniqueWords, chars, sentences, paragraphs, lines,
                avgWordLength, avgSentenceLength,
                readTime: readTimeDisplay, speakTime: speakTimeDisplay, fleschScore: flesch
            };

            // Frequency & N-Grams Analysis
            this.analyzeFrequency(wordsArray);
        },

        analyzeFrequency(allWords) {
            const wordMap = {};
            const pairMap = {};
            const normalizedWords = allWords.map(w => w.toLowerCase().replace(/[^a-z0-9-]/g, ''));

            // Single Words
            normalizedWords.forEach(w => {
                if (w.length < 2) return;
                if (this.ignoreStopWords && this.stopWordsList.includes(w)) return;
                
                wordMap[w] = (wordMap[w] || 0) + 1;
            });

            // 2-Grams (Pairs)
            for (let i = 0; i < normalizedWords.length - 1; i++) {
                const w1 = normalizedWords[i];
                const w2 = normalizedWords[i+1];
                if (w1.length < 2 || w2.length < 2) continue;
                // Don't form n-grams purely of stop words like "of the"
                if (this.ignoreStopWords && this.stopWordsList.includes(w1) && this.stopWordsList.includes(w2)) continue;

                const phrase = w1 + ' ' + w2;
                pairMap[phrase] = (pairMap[phrase] || 0) + 1;
            }

            // Sort & Slice Top Words
            const totalAnalyzed = Object.values(wordMap).reduce((a, b) => a + b, 0) || 1;
            this.topWords = Object.entries(wordMap)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 10)
                .map(([word, count]) => ({ 
                    word, 
                    count, 
                    pct: ((count / totalAnalyzed) * 100).toFixed(1) 
                }));

            // Sort & Slice 2-Grams
            this.top2Grams = Object.entries(pairMap)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 5) // Top 5 phrases
                .map(([phrase, count]) => ({ phrase, count }));
        }
    };
}

if (typeof window.Alpine !== 'undefined') {
    Alpine.data('wordCounter', wordCounter);
} else {
    document.addEventListener('alpine:init', () => {
        Alpine.data('wordCounter', wordCounter);
    });
}
